import { Loader } from '../../components/Loader';
import {
  Button,
  Card,
  Iconography,
  LinkButton,
  ShowMore,
  Typography,
} from '../../components/primitives';
import Alert from '../../components/ui/Alert';
import { useBlurbContext } from '../../context/Blurb.context';
import { useProjectsContext } from '../../context/projects.context';
import { testId } from '../../utils';
import { getDaysLeft } from '../../utils/project/days-left';
import { useProductDetails } from '../../utils/project/product';
import { Cornerstone } from '@rpi/api';
import { Trans, useTranslation } from '@rpi/i18n';
import React from 'react';

type ProjectCardProps = {
  project: Cornerstone.ProjectResult;
  onOrderMore: (project: Cornerstone.ProjectResult) => void;
  deleteProject: (id: number) => void;
};

const useProjectError = (project: Cornerstone.ProjectResult) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.projects' });
  const { addItemMutation } = useProjectsContext();

  if (
    addItemMutation.error &&
    addItemMutation.variables?.data.projectId === project.id
  ) {
    return t('order_failed');
  }

  return undefined;
};

const ActionButtons: React.FC<
  Pick<ProjectCardProps, 'project' | 'onOrderMore'>
> = ({ project, onOrderMore }) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'route.projects',
  });

  const error = useProjectError(project);

  const onOrder = React.useCallback(
    () => onOrderMore(project),
    [project, onOrderMore],
  );

  const daysLeft = getDaysLeft(new Date(project.createdAt));

  return (
    <div className="flex flex-col gap-3 w-64">
      <div className="flex justify-end">
        <Button
          className="max-w-36"
          {...testId('project', 'button', 'order')}
          size="sm"
          onClick={onOrder}
        >
          {t('order')}
        </Button>
      </div>
      {error && (
        <Alert {...testId('project', 'error')} variant="error">
          {error}
        </Alert>
      )}
      {!error && !project.orderedAt && daysLeft && (
        <Alert variant="error" {...testId('retention-alert')}>
          <Trans
            i18nKey={
              daysLeft === 1
                ? 'route.projects.retention_alert_one'
                : 'route.projects.retention_alert'
            }
            values={{ daysLeft: daysLeft }}
            components={{
              'retention-link': (
                <a
                  className="text-red-dark"
                  aria-label="retention-link"
                  href="https://support.blurb.com/hc/en-us/articles/207799786-How-long-will-Blurb-store-my-book"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </Alert>
      )}
    </div>
  );
};

const ActionLinks: React.FC<
  Pick<ProjectCardProps, 'project' | 'deleteProject'>
> = ({ project, deleteProject }) => {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2">
      <LinkButton
        {...testId('project', 'link', 'delete-project')}
        onClick={() => project.id && deleteProject(project.id)}
      >
        {t('route.user.addresses.delete')}
      </LinkButton>
    </div>
  );
};

const ProjectDetails: React.FC<Pick<ProjectCardProps, 'project'>> = ({
  project,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.cart' });

  const { productDetailsStatus, details } = useProductDetails(
    project.sku || '',
  );

  return (
    <>
      {productDetailsStatus === 'error' && (
        <Alert variant="error" data-testid="product-rules-error">
          {t('show_details_error')}
        </Alert>
      )}

      <ShowMore
        title={t('show_details')}
        content={
          <div className="list-none mt-2 flex flex-col gap-2">
            {project.author && (
              <div className="flex gap-1">
                <Typography variant="body-semibold">{t('author')}:</Typography>
                <Typography variant="body">{project.author}</Typography>
              </div>
            )}
            {details.map((value, key) => (
              <div key={key} className="flex gap-1">
                <Typography variant="body-semibold">{value[0]}: </Typography>
                <Typography variant="body">{value[1]}</Typography>
              </div>
            ))}
            {project.pageCount !== undefined && (
              <div className="flex gap-1">
                <Typography variant="body-semibold">
                  {t('page_count')}:
                </Typography>
                <Typography variant="body">
                  {project.pageCount?.toString()}
                </Typography>
              </div>
            )}
          </div>
        }
        data-testid="show-details-accordion"
      />
    </>
  );
};

const ProjectThumbnail: React.FC<Pick<ProjectCardProps, 'project'>> = ({
  project,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.projects' });
  const [isLoading, setIsLoading] = React.useState(true);
  const [showPlaceholder, setShowPlaceholder] = React.useState(
    !project.thumbnailUrl,
  );

  const onImgLoad = React.useCallback(() => {
    setIsLoading(false);
    setShowPlaceholder(false);
  }, []);

  const onImgError = React.useCallback(() => {
    setShowPlaceholder(true);
    setIsLoading(false);
  }, []);

  if (showPlaceholder) {
    return (
      <div
        {...testId('project', 'thumbnail', 'placeholder')}
        className="w-full md:w-[140px] h-[168px] bg-gray-light flex flex-col justify-center items-center"
      >
        <Iconography variant="hide_image" className="text-4xl text-gray-dark" />
        <Typography variant="footnote-2" className="text-gray-dark mt-2">
          {t('no_image')}
        </Typography>
      </div>
    );
  }

  return (
    <div
      {...testId('project', 'thumbnail')}
      className="w-full md:w-[140px] max-h-[168px] relative"
    >
      {isLoading && (
        <div className="w-full h-[168px] absolute flex justify-center items-center bg-gray-light">
          <Loader size="lg" />
        </div>
      )}
      <img
        {...testId('project', 'thumbnail', 'img')}
        className="w-full h-full object-contain"
        src={project.thumbnailUrl ?? ''}
        alt={project.title}
        onLoad={onImgLoad}
        onError={onImgError}
      />
    </div>
  );
};

export const GenericProjectCard: React.FC<ProjectCardProps> = (props) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route' });
  const { project, deleteProject } = props;
  const { settings } = useBlurbContext();
  const timeZone = Intl.DateTimeFormat(settings?.languageCode || 'en-US', {
    timeZoneName: 'short',
  })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value;
  return (
    <Card {...testId('project')}>
      <div
        data-projectid={project.id}
        className="flex flex-col md:flex-row md:justify-between gap-4 md:gap-10 p-6 md:p-8 items-center md:items-start"
      >
        <ProjectThumbnail project={project} />
        <div
          {...testId('project', 'details')}
          className="flex-grow flex flex-col gap-4"
        >
          <Typography
            variant="body-large-semibold"
            {...testId('project', 'details', 'title')}
          >
            {project.title}
          </Typography>
          <div className="flex flex-col">
            <div className="flex gap-1">
              <Typography variant="body-semibold">
                {t('projects.project')}:
              </Typography>
              <Typography variant="body">{project.projectType}</Typography>
            </div>
            <div className="flex gap-1">
              <Typography variant="body-semibold">
                {t('projects.created_with')}:
              </Typography>
              <Typography variant="body">{project.creationSource}</Typography>
            </div>
            {project.updatedAt && (
              <div className="flex gap-1">
                <Typography variant="body-semibold">
                  {t('projects.uploaded')}:
                </Typography>
                <Typography
                  variant="body"
                  {...testId('project', 'details', 'date')}
                >
                  {new Date(project.updatedAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                  {timeZone && ` ${timeZone}`}
                </Typography>
              </div>
            )}
          </div>
          <ActionLinks project={project} deleteProject={deleteProject} />
          <ProjectDetails project={project} />
        </div>
        <ActionButtons {...props} />
      </div>
    </Card>
  );
};
